import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as ObjectActions from './advanced-filter.actions';
import * as logbookAppReducer from '../logbook.reducer';
import * as AppActions from '../../store/app/actions';
import { AdvancedFilterService } from '../../shared/component/filter/advanced-filter/advanced-filter.service';
import { IConfig, User } from '../user/model';
import { catchError, of } from 'rxjs';
import { IFilter } from '../../shared/component/filter/advanced-filter/advanced-filter.model';
import _ from 'lodash';
import { emptyUserMeta } from '../../../constants';
import { IBaseOneResponse } from '../../shared/model/interface/crud-response-interface.model';
import { ICustomAdvancedFilterDefaultResponse } from './advanced-filter.model';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class AdvancedFilterEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<logbookAppReducer.LogbookAppState>,
    private readonly advancedFilterService: AdvancedFilterService,
  ) {}

  public saveFilterConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.SAVE_FILTER_CONFIG),
      switchMap((objectData: ObjectActions.SaveFilterConfig) => {
        const normalizedFilters: IFilter[] = _.cloneDeep(objectData).filters.map(
          (filter: IFilter): IFilter => ({
            ...filter,
            operators: undefined,
            value: this.advancedFilterService.prepareValue(filter),
          }),
        );
        let userConfig!: IConfig | null;

        this.store
          .select('user')
          .pipe(take(1))
          .subscribe((state: User) => {
            userConfig = { ..._.cloneDeep(emptyUserMeta), ..._.cloneDeep(state.configuration) };
          });
        Object.assign(userConfig?.filterConfig, { [objectData.pageName]: normalizedFilters });

        return this.advancedFilterService.setAsDefault(userConfig).pipe(
          switchMap((response: any) => {
            return of(new ObjectActions.SaveFilterConfigCompleted(response), new AppActions.HideLoader());
          }),
        );
      }),
    ),
  );

  getCustomFilterConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.GET_CUSTOM_FILTER_CONFIG),
      switchMap((objectData: ObjectActions.GetCustomFilterConfig) => {
        const httpParams = new HttpParams()
          .set('source_id', objectData.params.sourceId)
          .set('source_type', objectData.params.sourceType)
          .set('location', objectData.params.location);

        return this.advancedFilterService.getCustomFilterConfig(httpParams).pipe(
          switchMap((response: IBaseOneResponse<ICustomAdvancedFilterDefaultResponse>) => {
            return of(new ObjectActions.GetCustomFilterConfigCompleted(response.data));
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes));
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes));
      }),
    ),
  );

  addCustomFilterConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.ADD_CUSTOM_FILTER_CONFIG),
      switchMap((objectData: ObjectActions.AddCustomFilterConfig) => {
        let normalizedMeta: IFilter[] = [];
        if (objectData.params.meta.length) {
          normalizedMeta = _.cloneDeep(objectData.params.meta).map(
            (filter: IFilter): IFilter => ({
              ...filter,
              value: this.advancedFilterService.prepareValue(filter),
            }),
          );
        }

        return this.advancedFilterService.addCustomFilterConfig({ ...objectData.params, meta: normalizedMeta }).pipe(
          switchMap((response: IBaseOneResponse<ICustomAdvancedFilterDefaultResponse>) => {
            return of(new ObjectActions.AddCustomFilterConfigCompleted(response.data));
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes));
          }),
        );
      }),
      catchError((errorRes) => {

        return of(new ObjectActions.FetchError(errorRes));
      }),
    ),
  );

  updateCustomFilterConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.UPDATE_CUSTOM_FILTER_CONFIG),
      switchMap((objectData: ObjectActions.UpdateCustomFilterConfig) => {
        let normalizedMeta: IFilter[] = [];
        if (objectData.params.meta.length) {
          normalizedMeta = _.cloneDeep(objectData.params.meta).map(
            (filter: IFilter): IFilter => ({
              ...filter,
              value: this.advancedFilterService.prepareValue(filter),
            }),
          );
        }

        return this.advancedFilterService.updateCustomFilterConfig({ ...objectData.params, meta: normalizedMeta }).pipe(
          switchMap((response: IBaseOneResponse<ICustomAdvancedFilterDefaultResponse>) => {
            return of(new ObjectActions.UpdateCustomFilterConfigCompleted(response.data));
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes));
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes));
      }),
    ),
  );
}
